import { useState, useEffect } from 'react';
import { avatarImages } from '../constants';
import type { RoleplayStatus } from '../types';

export interface UseRoleplayAvatarParams {
  targetGender?: string;
  targetAge?: string;
  targetLanguage?: string;
  aiScore: number;
  status: RoleplayStatus;
  isRecording: boolean;
}

function getAvatarType(
  gender: string | undefined,
  age: string | undefined,
  language: string | undefined
): string {
  if (gender === undefined || age === undefined || language === undefined) {
    return 'default';
  }
  const genderType = gender === '男性' ? 'm' : 'w';
  const ageType = age === '40～50代' ? '40-50' : '20-30';
  const ethnicityType = language === '英語' ? 'en' : 'jp';
  return `${genderType}${ageType}_${ethnicityType}`;
}

function getAvatarImage(type: string, score: number, state: string): string {
  const avatarType = type || 'default';
  const avatarScore = score >= 0 && score <= 5 ? score : 0;
  const avatarState = state || 'wait';
  const entry = avatarImages[avatarType][avatarScore];
  if (typeof entry === 'string') return entry;
  return (entry as { listen?: string; wait?: string })[avatarState as 'listen' | 'wait'] ?? entry.wait ?? entry.listen ?? '';
}

export function useRoleplayAvatar(params: UseRoleplayAvatarParams): string {
  const { targetGender, targetAge, targetLanguage, aiScore, status, isRecording } = params;
  const [avatarImage, setAvatarImage] = useState<string>('');

  useEffect(() => {
    const avatarType = getAvatarType(targetGender, targetAge, targetLanguage);
    const displayScore = aiScore === 0 || status === 'aiSpeaking' ? 0 : aiScore;
    const avatarState = isRecording ? 'listen' : 'wait';
    setAvatarImage(getAvatarImage(avatarType, displayScore, avatarState));
  }, [targetGender, targetAge, targetLanguage, aiScore, status, isRecording]);

  return avatarImage;
}
